/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import ArgosFlexbox from '../../../components/Containers/ArgosFlexbox';
import ArgosCollapseComponent from '../../../components/CollapseComponent/ArgosCollapseComponent';
import {theme} from '../../../../constants/theme';
import ImageProperties from '../CollapsibleTabs/ImageProperties';
import ItemsReferencing from '../CollapsibleTabs/ItemsReferencing/ItemsReferencing';
import ChangeHistory from '../CollapsibleTabs/ChangeHistory';
import ArgosButton, {ArgosLoadingButton} from '../../../components/Buttons/ArgosButton';
import {useImagePostRequest, usePostRequest, usePostRequestWithJsonResponse} from '../../../../api/helper';
import {addObjectEndpoint, addRealWorldObjectImageEndpoint} from '../../objectApp/constants';
import AddIcon from '@mui/icons-material/Add';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks';
import {selectCurrentUser} from '../../../../redux/ducks/AccessControl/slice';
import {Autocomplete, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField} from '@mui/material';
import SignProperties from '../CollapsibleSignTabs/SignProperties';
import {useGetIsUserAdmin, useHasWriteAccessGivenKey} from '../../../../redux/ducks/AccessControl/accessControlHelpers';
import {EArgosRouteKeys} from '../../../../Routes/constants';
import {DELETE_RWO_ENDPOINT, DELETE_RWO_VERSION_TITLE_TEXT, DELETE_RWO_VERSION_WARNING_TEXT, VALUE_KEYS} from "../constants";
import {toast} from "react-hot-toast";
import ArgosDialog from "../../../components/Dialogs/ArgosDialog";
import {ArgosFavoriteIconLogic} from "../../../components/Buttons/ArgosFavoriteIconLogic";
import {ETrackedItemTypes} from "../../../../constants/trackedItems";
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../hooks/useSetRecentlyViewedWithDeps";
import Tooltip from "@mui/material/Tooltip";
import {ArgosEditIcon} from "../../../components/Icons/ArgosActionIcons";
import RoarReference from "./RoarReference";
import {StringParam, useQueryParam} from "use-query-params";
import RealWorldExample from './RealWorldExample';

/**
 * Renders the Sign ID input field.
 * @param objectId - The ID of the object.
 * @param inputStyle - The style to be applied to the input field.
 * @returns A JSX element representing the Sign ID input field.
 * @description This function creates a disabled input field that displays the Sign ID.
 * The field is styled using the provided inputStyle. The label "Sign ID" is displayed above the input.
 */
export const renderSignId = ({ objectId, inputStyle }: { objectId: string | null, inputStyle: React.CSSProperties }) => {
  return (
    <div style={{ flex: 1, maxWidth: '325px', marginLeft: 'auto', alignSelf: 'flex-start'}}>
      <label htmlFor="objectId" style={{ display: 'block', marginBottom: '5px', color: '#0173CE' }}>
        Sign ID
      </label>
      <input
        id="objectId"
        type="text"
        disabled={true}
        value={objectId ?? ""}
        style={inputStyle}
      />
    </div>
  );
};

const SignsRWOContent = (props: any) => {

  // signMessages
  const [selectedSignMessages, setSelectedSignMessages] = useState<string[]>(props.object?.signMessages?.split(",").map((item: string) => item.trim()) ?? []);
  const messages = props.object?.signMessagesList || [];
  const [signMessages, setSignMessages] = useState(props.object?.signMessagesList);

  // messageType
  const [selectedMessageType, setSelectedMessageType] = useState<string[]>(props.object?.messageType?.split(",").map((item: string) => item.trim()) ?? []);
  const messageTypesList = props.object?.messageTypeList || [];
  const [messageTypes, setMessageTypes] = useState(props.object?.signMessagesList);

  // signShapes
  const [selectedSignShapes, setSelectedSignShapes] = useState<string[]>(props.object?.signShape?.split(",").map((item: string) => item.trim()) ?? []);
  const signShapeList = props.object?.signShapeList || [];
  const [signShapes, setSignShapes] = useState(props.object?.signShapeList);

  // signColors 
  const [selectedSignColors, setSelectedSignColors] = useState<string[]>(props.object?.knownSignColor?.split(",").map((item: string) => item.trim()) ?? []);
  const signColorsList = props.object?.signColorsList || [];
  const [signColors, setSignColors] = useState(props.object?.knownSignColorList);

  const triggerReRender = props.triggerReRender
  const navigate = useNavigate();
  const dispatch = useAppDispatch()
  const currentUserGmid = useAppSelector(state => selectCurrentUser(state).id)


  // const [objectName, setObjectName] = useState<string>('');
  const [objectName, setObjectName] = useQueryParam('objectName', StringParam);
  const [objectId, setObjectId] = useQueryParam(VALUE_KEYS.ID, StringParam);
  const [componentDataMap, setComponentDataMap] = useState<any>({});
  const [editableCell, setEditableCell] = useState<{ rowIndex: number; cellKey: string } | null>(null);
  const [tableData, setTableData] = useState({
    axisDefinition: props?.object?.axisDefinition || '',
    references: props?.object?.references || '',
    requestSource: props?.object?.requestSource || '',
    comments: props?.object?.parameters?.general?.comments || ''
  });
  const uploadImageHandle = useImagePostRequest(addRealWorldObjectImageEndpoint);
  const [data, setData] = useState<any>({});
  const [mergedData, setMergedData] = useState<any>({});
  const [imageData, setImageData] = useState<any[]>([]);
  const [fileFormData, setFileFormData] = useState<FormData[]>([]);
  const [isEnabled, setIsEnabled] = useState<any>(false);
  const [resetTable, setResetTable] = useState<any>(false);
  const [renderKey, setRenderKey] = useState<number>(0);
  const isAdmin = useGetIsUserAdmin();
  const useHasWriteAccessGivenKeyValue = useHasWriteAccessGivenKey(EArgosRouteKeys.RWO_SIGNS)

  // managing the dialog for editing sign name
  // const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState<{ open: boolean, field: string, value: string }>({open: false, field: '', value: ''});
  const [isObjectNameUpdated, setIsObjectNameUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const updateRequest = usePostRequestWithJsonResponse(addObjectEndpoint);


  useEffect(() => {

    // Ensure fileFormData is an array and initialized if needed
    if (!fileFormData || !Array.isArray(fileFormData)) {
      setFileFormData([]);
    } else {
      const updatedFileFormData = [...fileFormData]; // Clone current state

      // Go through each element of the imageData array
      for (let i = 0; i < imageData.length; i++) {
        const element = imageData[i];

        if (Array.isArray(element)) {
          // If the element is an array, iterate through each FormData object inside it
          element.forEach((formData, index) => {
            if (formData instanceof FormData) {

              // Ensure a new FormData is created for each index
              if (!updatedFileFormData[index]) {
                updatedFileFormData[index] = new FormData();
              }

              // Append each key-value pair from FormData to the appropriate index
              for (const [key, value] of formData.entries()) {
                updatedFileFormData[index].append(key, value);
              }
            }
          });
        } else if (element && typeof element === "object" && "pendingImages" in element) {
          const {pendingImages} = element;

          if (Array.isArray(pendingImages)) {
            // Iterate through each pendingImage, which should be a FormData object
            for (let j = 0; j < pendingImages.length; j++) {
              const pendingImage = pendingImages[j];
              if (pendingImage instanceof FormData) {

                // Ensure a new FormData is created for each index
                if (!updatedFileFormData[j]) {
                  updatedFileFormData[j] = new FormData();
                }

                // Append each key-value pair from FormData to the appropriate index
                for (const [key, value] of pendingImage.entries()) {
                  updatedFileFormData[j].append(key, value);
                }
              }
            }
          }
        } else if (element instanceof FormData) {
          // If the element itself is a FormData object

          // Ensure a new FormData is created for this index
          if (!updatedFileFormData[i]) {
            updatedFileFormData[i] = new FormData();
          }

          // Append each key-value pair from FormData to the appropriate index
          for (const [key, value] of element.entries()) {
            updatedFileFormData[i].append(key, value);
          }
        }
      }

      // Set the updated fileFormData state
      setFileFormData(updatedFileFormData);
    }
  }, [imageData]);

  const handleDataUpdate = useCallback((updatedData: any) => {
    setData((prevState: any) => {
      // Merge the new data into the existing state, ensuring we only append if `updatedData` is not empty
      const mergedData = {
        ...prevState,
        ...(updatedData && Object.keys(updatedData).length > 0 ? updatedData : {}), // Append new data only if it's not empty
      };

      return mergedData;
    });
  }, [])

  const createCollapsibleTabs = () => {
    let _componentDataMap: any = {};
    // _componentDataMap['Sign Size Variants'] = <SignProperties onDataUpdate={handleDataUpdate} wipeSignPropertiesTable={wipeSignPropertiesTable} onSignTablePropertiesUpdate={wipeSignPropertiesTable} onSignDataUpdate={handleSignDataUpdate} onCellChangeParams={handleCellChangeParameters} {...props} style={{ overflow: 'hidden' }} />;
    _componentDataMap['Sign Size Variants'] =
      <SignProperties
        signSizes={props?.object?.signSizes}
        usageSpeeds={props?.object?.usageSpeeds}
        key={renderKey}
        onDataUpdate={handleDataUpdate}
        // onSignTablePropertiesUpdate={handleSignPropertiesTableUpdate}
        onSignDataUpdate={handleSignDataUpdate}
        resetTable={resetTable}
        onCellChangeParams={handleCellChangeParameters}
        wipeSignPropertiesTable={wipeSignPropertiesTable}
        {...props}
        style={{overflow: 'hidden'}}
      />
    _componentDataMap['Images'] = <ImageProperties onDataUpdate={handleDataUpdate} onImageUpdate={handleImageUpdate} {...props} style={{overflow: 'hidden'}}/>;
    _componentDataMap['Real World Example'] = <RealWorldExample object={props.object} onDataUpdate={handleDataUpdate}/>;
    _componentDataMap['ROAR Reference'] = <RoarReference object={props.object} onDataUpdate={handleDataUpdate}/>;
    _componentDataMap['Items Referencing Object'] = <ItemsReferencing style={{overflow: 'hidden'}} object={props.object}/>;
    _componentDataMap['Change History'] = <ChangeHistory onDataUpdate={handleDataUpdate} {...props} isSignType={true} style={{overflow: 'hidden'}}/>;
    setComponentDataMap(_componentDataMap);
  };

  useEffect(() => {
    setObjectName(props?.object?.signName || '');
    setSignMessages(props?.object?.signMessagesList || '');
    setSelectedSignMessages(props.object?.signMessages?.split(",").map((item: string) => item.trim()) ?? [])

    setMessageTypes(props?.object?.messageTypeList || '');
    setSelectedMessageType(props.object?.messageType?.split(",").map((item: string) => item.trim()) ?? [])

    setSignShapes(props?.object?.signShapeList || '');
    setSelectedSignShapes(props.object?.signShape?.split(",").map((item: string) => item.trim()) ?? [])

    setSignColors(props?.object?.knownSignColorList || '');
    setSelectedSignColors(props.object?.knownSignColor?.split(",").map((item: string) => item.trim()) ?? [])

    setInitialShapesSet(props.object?.signShape?.split(",").map((item: string) => item.trim()) ?? []);

    setTableData({
      axisDefinition: props?.object?.axisDefinition || '',
      references: props?.object?.references || '',
      requestSource: props?.object?.requestSource || '',
      comments: props?.object?.comments || ''
    });
    createCollapsibleTabs();
  }, [props, handleDataUpdate]);


  /**
   * Handles updating the object name by sending a request to the backend.
   *
   * This function resets the error message, prepares the request payload,
   * and sends a POST request to update the object name. If successful,
   * it closes the update dialog and refreshes the UI; otherwise, it sets an error message.
   */
  const objectNameRef = useRef<HTMLInputElement>(null);
  const [isUpdatingObjectName, setIsUpdatingObjectName] = useState(false);
  const handleUpdateSignName = async (oldFieldValue: any, fieldKey: string) => {
    setErrorMessage(""); // Reset error message

    const newObjectName = objectNameRef.current?.value || "";

    const dataToSubmit = {
      oldFieldValue,
      newFieldValue: newObjectName,
      fieldKey,
      type: props.object?.type,
      objectId: props.object?.id,
    }

    try {
      setIsUpdatingObjectName(true)

      // Use the function returned by the hook inside the event handler
      const response = await updateRequest(dataToSubmit, null, "POST", "?action=updateField");

      const responseData = await response.json();  // Read JSON response

      if (!response.ok) {
        throw new Error(responseData.message || "Error updating");
      }

      setShowUpdateDialog({open: false, field: '', value: ''}); // Close dialog
      setObjectName(newObjectName)
      triggerReRender(); // Refresh UI
    } catch (error: any) {
      toast.error("Failed to update name");
      setErrorMessage(error.message);
    } finally {
      setIsUpdatingObjectName(false)
    }
  };


  const handleDoubleClick = (rowIndex: number, cellKey: string) => {
    setEditableCell({rowIndex, cellKey});
  };

  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>, cellKey: string) => {
    setIsEnabled(true);
    setTableData((prev) => ({
      ...prev,
      [cellKey]: e.target.value
    }));
  };

  const handleBlur = () => {
    setEditableCell(null);
  };

  const handleCellChangeParameters = (newState: boolean) => setIsEnabled(newState);


  const handleSignDataUpdate = (updatedData: any) => {
    setData((prevState: any) => {
      // Merge the new data into the existing state, ensuring we only append if `updatedData` is not empty
      const mergedData = {
        ...prevState, // Preserve existing data
        ...(updatedData && Object.keys(updatedData).length > 0 ? updatedData : {}), // Append new data only if it's not empty
      };

      // Log the new state after merging

      return mergedData;
    });
  };


  const handleImageUpdate = (updatedImage: FormData) => {
    setIsEnabled(true);
    setImageData([updatedImage]);
  };

  const handleSubmit = async (isAdminEdit: boolean) => {
    setIsEnabled(false);

    // Create a new FormData object
    const combinedFormData = new FormData();

    fileFormData.forEach((formData, index) => {
      formData.forEach((value, key) => {
        // Check if the key is 'file'
        if (key === 'file') {
          combinedFormData.append('file', value); // Append the file directly as 'file'
        } else {
          const uniqueKey = `${index}[${key}]`;
          combinedFormData.append(uniqueKey, value);
        }
      });
    });

    // Prepare the extendedData and dataToSubmit objects
    const extendedData = {
      ...data,
      ...tableData,
    };

    const signMessages = selectedSignMessages.join(", ");
    const messageType = selectedMessageType.join(", ");
    const signShape = selectedSignShapes.join(", ");
    const knownSignColor = selectedSignColors.join(", ");

    const mode = isAdminEdit ? "editExisting" : "newVersion";
    const dataToSubmit = {
      ...extendedData,
      signName: objectName,
      signMessages,
      messageType,
      signShape,
      knownSignColor,
      objectType: props.object.type,
      mode,
      author: currentUserGmid,
      id: props?.object?.id,
      latestVersion: props?.object?.latestVersion,
    };

    // Update the signVariants in dataToSubmit to maintain consistency with schema and not to include all values for references needed for table
    if (dataToSubmit.signVariants) {
      dataToSubmit.signVariants = dataToSubmit.signVariants.map((variant: any) => ({
        sizeID: variant.sizeID,
        usageSpeedID: variant.usageSpeedID,
        signSizeVariant: variant.signSizeVariant,
        width: variant.width,
        height: variant.height,
        length: variant.length || "",  // Default to empty string if not provided
        id: variant.id,
        comments: variant.comments || "",
      }));
    }

    // Append dataToSubmit as JSON to combinedFormData
    combinedFormData.append(
      "json",
      new Blob([JSON.stringify(dataToSubmit)], {type: "application/json"})
    );

    try {
      const response = await uploadImageHandle(combinedFormData);

      if (response?.updated_items) {
        const id = Array.isArray(response.updated_items)
          ? response.updated_items[0]?.id
          : response.updated_items?.id;

        if (props.fetchBaselineData) {
          props.fetchBaselineData();
        }
      }
    } catch (error) {
      console.error("Failed to add new version:", error);
      toast.error("Failed to add new version");
      setIsEnabled(true)
    }
  };


  useEffect(() => {
    const defaultClassification = messages[0]?.trim() || '';
    if (!signMessages && defaultClassification) {
      setSignMessages(defaultClassification);
    }
  }, [messages]);


  // test isEnabled is set after add buttton is clicked for objectClassification drop down
  useEffect(() => {
    if (selectedSignMessages.length > 0) {
      setIsEnabled(true);
    }
    if (selectedMessageType.length > 0) {
      setIsEnabled(true);
    }
    if (selectedSignShapes.length > 0) {
      setIsEnabled(true);
    }
    if (selectedSignColors.length > 0) {
      setIsEnabled(true);
    }
    // else {
    //   setIsEnabled(false);
    // }
  }, [selectedSignMessages, selectedMessageType, selectedSignShapes, selectedSignColors]);


  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");


  const handleDoubleClickClassification = () => {
    setInputVisible(true); // Show input field on double-click
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    setInputVisible(false); // Hide input on blur if no enter is pressed
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      setSignMessages((prevClassification: any) => `${prevClassification}, ${inputValue.trim()}`);

      setInputValue(""); // Clear the input value
      setInputVisible(false); // Hide the input field
    }
  };

  const [initialShapesSet, setInitialShapesSet] = useState<string[]>(props.object?.signShape?.split(",").map((item: string) => item.trim()) ?? []);
  const [wipeSignPropertiesTable, setWipeSignPropertiesTable] = useState(false);

  useEffect(() => {
    createCollapsibleTabs();
  }, [resetTable, handleDataUpdate]); // Re-run when resetTable changes

  const [isLoadingDeleteRwoVersion, setIsLoadingDeleteRwoVersion] = useState(false);
  const deleteRwoVersionHandle = usePostRequest(DELETE_RWO_ENDPOINT)
  const [isDeleteRwoVersionDialogOpen, setIsDeleteRwoVersionDialogOpen] = useState(false);
  const onClickDeleteRwoVersion = (e: any) => {
    setIsDeleteRwoVersionDialogOpen(true)
  }
  /**
   * Handles the deletion of an rwo version.
   * @param e - The event object.
   */
  const handleDeleteRwoVersion = (e: any) => {
    if (!props.object?.id) {
      toast.error("RWO ID is not available");
      return;
    } else if (Number(props.object?.latestVersion) === 1) {
      // toast.error("Cannot delete the first version of the object");
      // return;
    }
    const payload = {
      deleteType: 'rwoVersion',
      rwoId: props.object?.id,
    }
    setIsLoadingDeleteRwoVersion(true)
    deleteRwoVersionHandle(payload).then((res: any) => {
      // if res has error key, then show error message
      if (res?.error) {
        // toast.error(res.error)
        toast.error((t) => (
          <div>
            <span>{res.error}</span>
            <ArgosButton
              style={{marginLeft: 5}}
              variant={'text'}
              onClick={() => toast.dismiss(t.id)}
            >
              Close
            </ArgosButton>
          </div>
        ), {duration: Infinity})
        return
      } else {
        props.fetchBaselineData()
      }
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to delete object version")
    }).finally(() => {
      setIsLoadingDeleteRwoVersion(false)
    })
  }

  /**
   * Favorites and recently viewed
   */
  const trackedItemUid = props.object?.id
  const trackedItemType = ETrackedItemTypes.RWO_SIGNS
  const favIcon = <ArgosFavoriteIconLogic favoriteType={trackedItemType} trackedItemUid={trackedItemUid} partitionKey={trackedItemUid}/>
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid,
    partitionKey: trackedItemUid,
    trackedItemType,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)


  const signTypeRef = useRef<HTMLInputElement>(null);
  const knownOfficialSignDesignationsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (signTypeRef.current) signTypeRef.current.value = props.object.signType ?? "";
    if (knownOfficialSignDesignationsRef.current) knownOfficialSignDesignationsRef.current.value = props.object.knownOfficialSign ?? "";
  }, [props.object]);

  const handleUpdate = () => {
    handleDataUpdate({
      signType: signTypeRef.current?.value ?? "",
      knownOfficialSign: knownOfficialSignDesignationsRef.current?.value ?? "",
    });
  };

  const columns = [
    {label: "References", key: "references"},
    {label: "Object Request Source", key: "requestSource"},
    {label: "Comments", key: "comments"},
  ];

  const inputStyle = {
    width: `100%`, // +2 for padding/cursor
    minWidth: '80px',
    maxWidth: '100%',
    padding: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    minHeight: '48px'
  };

  return (
    <div style={{overflow: 'auto'}}>
      <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
        <div style={{padding: '10px'}}>
          {/* Sign name */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '10px'}}>
            <ArgosFlexbox vertical style={{width: '100%'}}>
              {/* Object Name Input */}
              <div style={{flex: 1, minWidth: 'auto', display: 'flex', alignItems: 'center'}}>
                <div style={{flexGrow: 1, maxWidth: '60%'}}>
                  <label htmlFor="objectName" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                    Sign Name
                  </label>
                  <input
                    id="objectName"
                    type="text"
                    disabled={true}
                    value={objectName ?? ""}
                    style={inputStyle}
                  />
                </div>
                {useHasWriteAccessGivenKeyValue && (
                  <ArgosFlexbox style={{marginTop: 20}}>
                    <Tooltip title='Update Sign Name' placement='top' arrow>
                      <IconButton
                        style={{width: 60, height: 60}}
                        onClick={() => {
                          setErrorMessage("");
                          setShowUpdateDialog({open: true, field: 'signName', value: objectName ?? ''});
                        }}
                      >
                        <ArgosEditIcon style={{width: 30, height: 30}}/>
                      </IconButton>
                    </Tooltip>
                  </ArgosFlexbox>
                )}
                 {/* Sign ID Input */}
                 {renderSignId({ objectId: objectId ?? null, inputStyle })}

                {/*Favorite*/}
                <div style={{ alignSelf: 'flex-start', marginTop: 35, marginLeft: 10 }}>
                  {favIcon}
                </div>
              </div>

              {/* Popup Dialog for Updating Field (currently only Sign Name) */}
              <Dialog
                open={!!showUpdateDialog.open}
                onClose={() => setShowUpdateDialog({open: false, field: '', value: ''})}
                TransitionProps={{
                  onEntered: () => {
                    if (objectNameRef.current) {
                      objectNameRef.current.value = showUpdateDialog.value || "";
                      objectNameRef.current.focus();
                    }
                  },
                }}
              >
                <DialogTitle>
                  {showUpdateDialog.field === 'signName' && "Update Sign Name"}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {showUpdateDialog.field === 'signName' && "Enter a new name for the object. This will replace the name for all versions of this object."}
                  </DialogContentText>
                  <TextField
                    style={{marginTop: 20}}
                    focused={true}
                    margin="dense"
                    label={showUpdateDialog.field === 'signName' ? "Sign Name" : ''}
                    type="text"
                    fullWidth
                    inputRef={objectNameRef}
                    error={!!errorMessage}
                    helperText={errorMessage}
                  />
                </DialogContent>
                <DialogActions>
                  <ArgosButton disabled={isUpdatingObjectName} onClick={() => setShowUpdateDialog({open: false, field: '', value: ''})} color="secondary">
                    Cancel
                  </ArgosButton>
                  <ArgosLoadingButton
                    isLoading={isUpdatingObjectName}
                    onClick={() => handleUpdateSignName(showUpdateDialog.value, showUpdateDialog.field)}
                    color="primary"
                  >
                    Save
                  </ArgosLoadingButton>
                </DialogActions>
              </Dialog>
            </ArgosFlexbox>

            <ArgosFlexbox style={{alignSelf: 'flex-end'}}>

            </ArgosFlexbox>
          </div>

          {/*Sign Messages dropdown; Delete and add version buttons; favorite button*/}
          <ArgosFlexbox style={{width: '100%', marginBottom: 5, padding: '20px 10px 10px 0', alignItems: 'center', overflow: 'auto'}}>
            {/*Delete Version, Save New Version, Favorite Icon*/}
            <ArgosFlexbox>
              <ArgosButton
                onClick={onClickDeleteRwoVersion}
                style={{
                  minWidth: '180px',
                  height: '48px'
                }}
              >
                Delete Version
              </ArgosButton>

              {/* Save New Version Button */}
              {useHasWriteAccessGivenKeyValue && (
                <ArgosButton
                  style={{minWidth: '180px', height: '48px', alignSelf: 'center'}}
                  onClick={()=>handleSubmit(false)}
                  disabled={!isEnabled}
                >
                  Save New Version
                </ArgosButton>
              )}

              {/*Admin edit button*/}
              {isAdmin && (
                <ArgosButton
                  style={{minWidth: '180px', height: '48px', alignSelf: 'center', marginLeft: 10}}
                  onClick={() => handleSubmit(true)}
                >
                  Admin Edit
                </ArgosButton>
              )}
            </ArgosFlexbox>
          </ArgosFlexbox>

          <div style={{flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', marginBottom: 10}}>
            <div style={{flexGrow: 1}}>
              <label htmlFor="signType" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                Sign Type
              </label>
              <input
                id="signType"
                type="text"
                ref={signTypeRef}
                defaultValue={props.object.signType ?? ""}
                onBlur={handleUpdate}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', marginBottom: 10}}>
            <div style={{flexGrow: 1}}>
              <label htmlFor="knownOfficialSign" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                Known Official Sign Designations
              </label>
              <input
                id="knownOfficialSign"
                type="text"
                ref={knownOfficialSignDesignationsRef}
                defaultValue={props.object.knownOfficialSign ?? ""}
                onBlur={handleUpdate}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{display: 'flex', flexWrap: 'wrap'}}>
            {/* messageType Drop Down */}
            <div style={{flex: '1 1 100%', minWidth: '100px', marginBottom: '10px', paddingRight: '14px'}}>
              <label htmlFor="messageType" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                Message Type
              </label>

              {inputVisible ? (
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  onKeyPress={handleKeyPress} // Handle Enter key press
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', position: 'absolute', right: '-200px'}}
                  autoFocus
                />
              ) : (
                <Autocomplete
                  multiple  // Enable multiple selections
                  id="messageType"
                  // options={signMessages.split(',').map((classification: string) => classification.trim())}
                  options={messageTypesList}
                  value={selectedMessageType?.filter((c) => c.trim() !== "") ?? []}
                  onChange={(event, newValue) => {
                    // Make sure that newValue is a unique array and set it
                    setSelectedMessageType([...new Set(newValue as string[])]);
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(props, option) => (
                    <li {...props} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span>
                        {option}
                      </span>

                      {/* Conditionally render "+" button if the item is not already in selectedClassifications */}
                      {!selectedMessageType.includes(option) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();  // Prevent navigation when "+" is clicked

                            // Add the option to selectedClassifications if it's not already there
                            if (!selectedMessageType.includes(option)) {
                              setSelectedMessageType((prev: any) => [...prev, option]);
                            }
                          }}
                        >
                          <AddIcon fontSize="small"/>
                        </IconButton>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      style={{width: '100%'}}
                    />
                  )}
                />
              )}
            </div>

            {/* signShape Drop Down */}
            <div style={{flex: '1 1 100%', minWidth: '100px', marginBottom: '10px', paddingRight: '14px'}}>
              <label htmlFor="signShape" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                Sign Shape
              </label>

              <Autocomplete
                multiple
                id="signShape"
                options={signShapeList}
                value={selectedSignShapes?.filter((c) => c.trim() !== "") ?? []}
                onChange={(event, newValue) => {
                  setSelectedSignShapes([...new Set(newValue)]);
                }}
                getOptionLabel={(option) => option}
                renderOption={(props, option) => (
                  <li {...props} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <span>
                      {option}
                    </span>

                    {!selectedSignShapes.includes(option) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!selectedSignShapes.includes(option)) {
                            setSelectedSignShapes((prev) => [...prev, option]);
                          }
                        }}
                      >
                        <AddIcon fontSize="small"/>
                      </IconButton>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    style={{width: '100%'}}
                  />
                )}
              />
            </div>


            {/* signColors Drop Down */}
            <div style={{flex: '1 1 100%', minWidth: '100px', marginBottom: '10px', paddingRight: '14px'}}>
              {/* <div style={{ flex: 1, minWidth: '200px', paddingLeft: '10px' }}> */}
              <label htmlFor="knownSignColor" style={{display: 'block', marginBottom: '5px', color: '#0173CE'}}>
                Sign Color
              </label>

              {inputVisible ? (
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  onKeyPress={handleKeyPress} // Handle Enter key press
                  style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', position: 'absolute', right: '-200px'}}
                  autoFocus
                />
              ) : (
                <Autocomplete
                  multiple  // Enable multiple selections
                  sx={{
                    // width: '100%',
                  }}
                  id="knownSignColor"
                  // options={signMessages.split(',').map((classification: string) => classification.trim())}
                  options={signColorsList}
                  value={selectedSignColors?.filter((c) => c.trim() !== "") ?? []}
                  onChange={(event, newValue) => {
                    // Make sure that newValue is a unique array and set it
                    setSelectedSignColors([...new Set(newValue as string[])]);
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(props, option) => (
                    <li {...props} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span>
                        {option}
                      </span>
                      {/* Conditionally render "+" button if the item is not already in selectedClassifications */}
                      {!selectedSignColors.includes(option) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();  // Prevent navigation when "+" is clicked

                            // Add the option to selectedClassifications if it's not already there
                            if (!selectedSignColors.includes(option)) {
                              setSelectedSignColors((prev: any) => [...prev, option]);
                            }
                          }}
                        >
                          <AddIcon fontSize="small"/>
                        </IconButton>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      style={{width: '100%'}}
                    />
                  )}
                />
              )}
            </div>

          </div>

          {/* Table Section */}
          <table style={{minWidth: '99%', borderCollapse: 'collapse', marginBottom: '20px', paddingRight: '10px', marginTop: 5}}>
            <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{border: "1px solid #ccc", padding: "8px"}}>
                  {column.label}
                </th>
              ))}
            </tr>
            </thead>
            <tbody>
            <tr>
              {columns.map((column, index) => (
                <td
                  key={index}
                  style={{border: "1px solid #ccc", padding: "8px"}}
                  onDoubleClick={() => handleDoubleClick(0, column.key)}
                >
                  {editableCell?.rowIndex === 0 && editableCell?.cellKey === column.key ? (
                    <input
                      type="text"
                      value={tableData[column.key as keyof typeof tableData]}
                      onChange={(e) => handleCellChange(e, column.key)}
                      onBlur={() => {
                        setTableData((prev) => ({
                          ...prev,
                          [column.key]: prev[column.key as keyof typeof tableData].trim() === "" ? "" : prev[column.key as keyof typeof tableData],
                        }));
                        handleBlur();
                      }}
                      autoFocus
                      style={{width: "100%", padding: "4px"}}
                    />
                  ) : (
                    (!tableData[column.key as keyof typeof tableData] || tableData[column.key as keyof typeof tableData].trim() === "")
                      ? "N/A"
                      : tableData[column.key as keyof typeof tableData]
                  )}
                </td>
              ))}
            </tr>
            </tbody>


          </table>
        </div>

        <div style={{flex: 1, overflow: 'auto'}}>
          <ArgosCollapseComponent
            styleProps={{
              outlineOnly: true,
              fontColor: theme.layout.sidebar.backgroundColor,
            }}
            defaultOpen={true}
            data={componentDataMap ?? {}}
          />
        </div>
      </ArgosFlexbox>

      <ArgosDialog
        open={isDeleteRwoVersionDialogOpen}
        width={'auto'} maxWidth={'300px'} height={'auto'}
        headerTitle={DELETE_RWO_VERSION_TITLE_TEXT} headerTitleAlign={'left'}
      >
        <span>{DELETE_RWO_VERSION_WARNING_TEXT}</span>
        <ArgosFlexbox justifyContent={'center'} style={{marginTop: 30}}>
          <ArgosButton disabled={isLoadingDeleteRwoVersion} onClick={() => setIsDeleteRwoVersionDialogOpen(false)}>Exit</ArgosButton>
          <ArgosLoadingButton isLoading={isLoadingDeleteRwoVersion} onClick={handleDeleteRwoVersion}>Ok</ArgosLoadingButton>
        </ArgosFlexbox>
      </ArgosDialog>
    </div>
  )
    ;
};

export default SignsRWOContent;
